import { useState, useEffect, RefObject } from 'react';

import { SELECTION_TYPES, SelectionType } from '../constants';
import { MultiSelectDropDownOption } from '../types';

export const useKeyboardNavigation = (
  isOpen: boolean,
  flattenedOptions: MultiSelectDropDownOption[],
  onItemSelect: (value: string, group?: string, type?: SelectionType, label?: string) => void,
  inputRef: RefObject<HTMLInputElement>,
  onClose: () => void,
) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          setFocusedIndex(-1);
          inputRef.current?.blur();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, flattenedOptions.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(flattenedOptions.length > 0 ? flattenedOptions.length - 1 : -1);
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < flattenedOptions.length) {
            const focusedOption = flattenedOptions[focusedIndex];
            onItemSelect(focusedOption.id, undefined, SELECTION_TYPES.INCLUDE, focusedOption.value);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, focusedIndex, flattenedOptions, onItemSelect, inputRef, onClose]);

  return { focusedIndex, setFocusedIndex };
};
